import { pillarHref, pillars } from '../data/pillars'

type PageMeta = { title: string; description: string }

const canonicalOrigin = 'https://syneural.com'

const defaultMeta: PageMeta = {
  title: 'SyNeural — Connected knowledge. Capable people. Coordinated action.',
  description:
    'SyNeural connects knowledge, people and institutional workflows. Through Noetivra, organizations coordinate services and turn insight into action, with workspaces for government, research, education, workforce and clinical work.',
}

const pillarMeta: Record<string, PageMeta> = Object.fromEntries(
  pillars.map((pillar) => [
    pillarHref(pillar.id),
    { title: `${pillar.name} — NCJI pillar ${pillar.order} — SyNeural`, description: pillar.description },
  ]),
)

const pageMeta: Record<string, PageMeta> = {
  ...pillarMeta,
  '/': defaultMeta,
  '/solutions': {
    title: 'Solutions — One connected system, configured for your work — SyNeural',
    description:
      'Seven solutions built on Noetivra: Government, Scientific Research, Education, Workforce, Leadership, Community and Clinical, each with dedicated workspaces and a demo to try.',
  },
  '/how-it-works': {
    title: 'How It Works — Connect knowledge, coordinate work, develop capability — SyNeural',
    description:
      'A plain-language explanation of SyNeural’s connected system: the Semantic Knowledge Platform, Noetivra Workspace and Core, shared modules and solution configurations.',
  },
  '/semantic-knowledge': {
    title: 'Semantic Knowledge Platform — SyNeural',
    description:
      'SKP connects and governs knowledge, sources, concepts, relationships and review history, keeping AI-assisted proposals distinct from approved knowledge.',
  },
  '/llm-vs-skp': {
    title: 'LLM vs. SKP: what’s the difference? — SyNeural',
    description: 'Why a language model and a governed knowledge system solve different problems, and how they work together.',
  },
  '/noetivra': {
    title: 'Noetivra — Modular software for connected work — SyNeural',
    description:
      'Noetivra is SyNeural’s modular software environment: a shared Core, dedicated Workspaces, configurable modules and solutions for each field. Interactive demos available; production platform under development.',
  },
  '/noetivra/demo-center': {
    title: 'Explore seven Noetivra demos — SyNeural',
    description:
      'Open interactive demos for government, research, education, talent, leadership, community and clinical review, all using fictional records saved in your browser.',
  },
  '/human-capability': {
    title: 'Human Capability Framework — SyNeural',
    description:
      'HCF structures assessment, human review, development and reassessment, and is the umbrella for CJI and NCJI methods and development pathways.',
  },
  '/cji': {
    title: 'Civic Judgment Index — SyNeural',
    description:
      'CJI defines structured reasoning and judgment assessment methods within the Human Capability Framework. Its six domains describe assessment scope under development.',
  },
  '/ncji': {
    title: 'NCJI — Five pillars of sound judgment — SyNeural',
    description:
      'NCJI organizes civic judgment assessment and development around five pillars within HCF, connecting findings with support, practice and reassessment.',
  },
  '/education': {
    title: 'Education & Learning — Make the next learning step clear — SyNeural',
    description:
      'Connect learning activities, feedback and development plans in one workspace, with an illustrative view of educator time released and a demo to try.',
  },
  '/employers': {
    title: 'Employers & Workforce — Noetivra Talent — SyNeural',
    description:
      'Bring role requirements, candidate review and employee development into one consistent workflow, with recorded hiring decisions and a Talent demo to try.',
  },
  '/leadership': {
    title: 'Leadership Development — SyNeural',
    description:
      'Decision exercises, coaching and purposeful practice with a visible development journey, shared across government, education and workforce programs.',
  },
  '/civil-society': {
    title: 'Community & Civil Society — SyNeural',
    description:
      'Bring mentorship, local knowledge, consent-based referrals and program review into a shared workspace while each organization keeps control of its records.',
  },
  '/government': {
    title: 'Government — National Capability and Development System — SyNeural',
    description:
      'NCDS brings citizen records, applications, referrals, support plans and program performance into a coordinated operating environment, with illustrative administrative savings scenarios.',
  },
  '/research': {
    title: 'Research behind connected knowledge and human development — SyNeural',
    description:
      'The methods, questions and studies behind SyNeural’s knowledge systems, HCF, CJI and NCJI, plus studies and validation, publication plans, research concepts and the roadmap.',
  },
  '/roadmap': {
    title: 'Development roadmap and publication plans — SyNeural',
    description:
      'What is available now, next development priorities, research and pilot priorities, and planned publications. Not a release schedule.',
  },
  '/about': {
    title: 'About SyNeural',
    description:
      'SyNeural’s mission, vision and leadership, and how SKP, the Human Capability Framework and Noetivra fit together across seven domains.',
  },
  '/investors': {
    title: 'Investment opportunity — SyNeural',
    description:
      'Proposed commercial models, illustrative revenue scenarios, current demonstration progress and next milestones for SyNeural.',
  },
  '/partnerships': {
    title: 'Partnerships — Pilot design, validation and integration — SyNeural',
    description:
      'Explore the existing demos and help shape production implementations through pilot design, validation and integration partnerships.',
  },
  '/contact': {
    title: 'Contact SyNeural',
    description: 'Discuss an SKP demonstration, framework research or a Noetivra pilot, or explore the self-guided demos first.',
  },
  '/privacy': {
    title: 'Privacy Policy — SyNeural',
    description: 'How SyNeural handles information on its website.',
  },
  '/terms': {
    title: 'Terms of Service — SyNeural',
    description: 'The terms that apply to use of the SyNeural website.',
  },
  '/individual-level': {
    title: 'Individual level — a proposed NCJI application — SyNeural',
    description: 'How NCJI within HCF is designed to help individuals understand their reasoning and follow development pathways.',
  },
  '/organizational-level': {
    title: 'Organizational level — a proposed NCJI application — SyNeural',
    description: 'How NCJI within HCF could support readiness, leadership pipelines and development inside institutions.',
  },
  '/municipal-scale': {
    title: 'Municipal scale — a proposed NCJI application — SyNeural',
    description: 'How aggregate NCJI views could inform local support, youth pathways and community programs, without judging individuals.',
  },
  '/national-scale': {
    title: 'National scale — a proposed NCJI application — SyNeural',
    description: 'How aggregate views could inform national planning for development, public services and institutional strengthening.',
  },
  '/projects': {
    title: 'Projects — Larger ideas. Connected systems. — SyNeural',
    description: 'Initiatives that bring SyNeural’s technology and research into broader civic and institutional settings, starting with Cognocracy.',
  },
  '/projects/cognocracy': {
    title: 'Cognocracy — A civic governance concept proposal — SyNeural',
    description:
      'Cognocracy is a civic project connecting SyNeural’s technology with a broader institutional framework. NCDS is its associated government solution.',
  },
  '/demos/government': {
    title: 'Government demo — Noetivra — SyNeural',
    description: 'Open the Government and National Capability and Development System demonstration.',
  },
  '/demos/research': {
    title: 'Noetivra Research demo — SyNeural',
    description: 'From sources to understanding: explore fictional studies, propose and review relationships, and inspect the evidence behind a knowledge map.',
  },
  '/demos/education': {
    title: 'Noetivra for Education demo — SyNeural',
    description: 'Build reasoning and self-direction through evidence, feedback and practice. An interactive prototype with fictional records.',
  },
  '/demos/talent': {
    title: 'Noetivra Talent demo — SyNeural',
    description: 'Connect role requirements to work samples, transparent review and development, with human decisions kept separate.',
  },
  '/demos/leadership': {
    title: 'Noetivra Leadership demo — SyNeural',
    description: 'Make consequential decisions inspectable, then connect feedback to coached practice.',
  },
  '/demos/community': {
    title: 'Noetivra for Communities demo — SyNeural',
    description: 'Connect personal goals, mentorship and practical support through respectful participation and purpose-limited referrals.',
  },
  '/demos/clinical': {
    title: 'Noetivra Clinical scoped demo — SyNeural',
    description: 'Structured documentation and professional review with one fictional patient. Documentation only — not diagnosis.',
  },
}

export function applyPageMeta(path: string) {
  const meta = pageMeta[path] ?? defaultMeta
  document.title = meta.title
  setMeta('name', 'description', meta.description)
  setMeta('property', 'og:title', meta.title)
  setMeta('property', 'og:description', meta.description)
  setMeta('property', 'og:url', `${canonicalOrigin}${path === '/' ? '' : path}`)
  setCanonical(`${canonicalOrigin}${path === '/' ? '/' : path}`)
}

function setMeta(attribute: 'name' | 'property', key: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attribute, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

function setCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', 'canonical')
    document.head.appendChild(link)
  }
  link.setAttribute('href', href)
}
